/// The 58 navigational stars — the set the Nautical Almanac tabulates
/// on its starred-body daily pages. Their Sidereal Hour Angle (SHA)
/// and declination drift slowly with precession (~1° per ~70 years
/// for SHA, far less for Dec), so the values below are mid-decade
/// epoch values rounded to 0.1° — accurate enough for plotting and
/// star ID. For real navigation use the current year's almanac.

export type Hemisphere = "N" | "S";

export interface NavStar {
  /// Star's common (navigational) name.
  name: string;
  /// Sidereal Hour Angle, degrees [0, 360). Measured westward from
  /// the First Point of Aries.
  shaDeg: number;
  /// Declination, degrees (N positive, S negative).
  decDeg: number;
  /// Apparent magnitude. Lower is brighter; magnitude 0 is roughly
  /// Vega-bright, magnitude 2.5 is at the edge of useful twilight
  /// visibility.
  magnitude: number;
  /// Modern constellation the star sits in.
  constellation: string;
}

export const NAV_STARS: readonly NavStar[] = Object.freeze([
  { name: "Acamar", shaDeg: 315.2, decDeg: -40.3, magnitude: 2.9, constellation: "Eridanus" },
  { name: "Achernar", shaDeg: 335.4, decDeg: -57.2, magnitude: 0.5, constellation: "Eridanus" },
  { name: "Acrux", shaDeg: 173.0, decDeg: -63.1, magnitude: 1.3, constellation: "Crux" },
  { name: "Adhara", shaDeg: 255.0, decDeg: -29.0, magnitude: 1.5, constellation: "Canis Major" },
  { name: "Aldebaran", shaDeg: 290.6, decDeg: 16.5, magnitude: 0.9, constellation: "Taurus" },
  { name: "Alioth", shaDeg: 166.4, decDeg: 55.9, magnitude: 1.8, constellation: "Ursa Major" },
  { name: "Alkaid", shaDeg: 152.6, decDeg: 49.3, magnitude: 1.9, constellation: "Ursa Major" },
  { name: "Al Na'ir", shaDeg: 27.6, decDeg: -46.9, magnitude: 1.7, constellation: "Grus" },
  { name: "Alnilam", shaDeg: 275.5, decDeg: -1.2, magnitude: 1.7, constellation: "Orion" },
  { name: "Alphard", shaDeg: 217.8, decDeg: -8.7, magnitude: 2.0, constellation: "Hydra" },
  { name: "Alphecca", shaDeg: 126.0, decDeg: 26.7, magnitude: 2.2, constellation: "Corona Borealis" },
  { name: "Alpheratz", shaDeg: 357.6, decDeg: 29.1, magnitude: 2.0, constellation: "Andromeda" },
  { name: "Altair", shaDeg: 62.1, decDeg: 8.9, magnitude: 0.8, constellation: "Aquila" },
  { name: "Ankaa", shaDeg: 353.1, decDeg: -42.3, magnitude: 2.4, constellation: "Phoenix" },
  { name: "Antares", shaDeg: 112.1, decDeg: -26.5, magnitude: 1.0, constellation: "Scorpius" },
  { name: "Arcturus", shaDeg: 145.8, decDeg: 19.2, magnitude: 0.0, constellation: "Boötes" },
  { name: "Atria", shaDeg: 107.3, decDeg: -69.0, magnitude: 1.9, constellation: "Triangulum Australe" },
  { name: "Avior", shaDeg: 234.0, decDeg: -59.5, magnitude: 1.9, constellation: "Carina" },
  { name: "Bellatrix", shaDeg: 278.2, decDeg: 6.3, magnitude: 1.6, constellation: "Orion" },
  { name: "Betelgeuse", shaDeg: 270.8, decDeg: 7.4, magnitude: 0.5, constellation: "Orion" },
  { name: "Canopus", shaDeg: 263.9, decDeg: -52.7, magnitude: -0.7, constellation: "Carina" },
  { name: "Capella", shaDeg: 280.4, decDeg: 46.0, magnitude: 0.1, constellation: "Auriga" },
  { name: "Deneb", shaDeg: 49.3, decDeg: 45.4, magnitude: 1.3, constellation: "Cygnus" },
  { name: "Denebola", shaDeg: 182.6, decDeg: 14.5, magnitude: 2.1, constellation: "Leo" },
  { name: "Diphda", shaDeg: 348.9, decDeg: -17.9, magnitude: 2.0, constellation: "Cetus" },
  { name: "Dubhe", shaDeg: 193.7, decDeg: 61.7, magnitude: 1.8, constellation: "Ursa Major" },
  { name: "Elnath", shaDeg: 278.1, decDeg: 28.6, magnitude: 1.7, constellation: "Taurus" },
  { name: "Eltanin", shaDeg: 90.5, decDeg: 51.5, magnitude: 2.2, constellation: "Draco" },
  { name: "Enif", shaDeg: 33.6, decDeg: 9.9, magnitude: 2.4, constellation: "Pegasus" },
  { name: "Fomalhaut", shaDeg: 15.2, decDeg: -29.5, magnitude: 1.2, constellation: "Piscis Austrinus" },
  { name: "Gacrux", shaDeg: 172.0, decDeg: -57.1, magnitude: 1.6, constellation: "Crux" },
  { name: "Gienah", shaDeg: 175.6, decDeg: -17.5, magnitude: 2.6, constellation: "Corvus" },
  { name: "Hadar", shaDeg: 148.7, decDeg: -60.4, magnitude: 0.6, constellation: "Centaurus" },
  { name: "Hamal", shaDeg: 327.8, decDeg: 23.5, magnitude: 2.0, constellation: "Aries" },
  { name: "Kaus Australis", shaDeg: 83.5, decDeg: -34.4, magnitude: 1.9, constellation: "Sagittarius" },
  { name: "Kochab", shaDeg: 137.3, decDeg: 74.1, magnitude: 2.1, constellation: "Ursa Minor" },
  { name: "Markab", shaDeg: 13.5, decDeg: 15.2, magnitude: 2.5, constellation: "Pegasus" },
  { name: "Menkar", shaDeg: 314.0, decDeg: 4.1, magnitude: 2.5, constellation: "Cetus" },
  { name: "Menkent", shaDeg: 148.0, decDeg: -36.4, magnitude: 2.1, constellation: "Centaurus" },
  { name: "Miaplacidus", shaDeg: 221.5, decDeg: -69.7, magnitude: 1.7, constellation: "Carina" },
  { name: "Mirfak", shaDeg: 308.6, decDeg: 49.9, magnitude: 1.8, constellation: "Perseus" },
  { name: "Nunki", shaDeg: 75.7, decDeg: -26.3, magnitude: 2.0, constellation: "Sagittarius" },
  { name: "Peacock", shaDeg: 53.1, decDeg: -56.7, magnitude: 1.9, constellation: "Pavo" },
  { name: "Polaris", shaDeg: 314.6, decDeg: 89.3, magnitude: 2.0, constellation: "Ursa Minor" },
  { name: "Pollux", shaDeg: 243.2, decDeg: 27.9, magnitude: 1.2, constellation: "Gemini" },
  { name: "Procyon", shaDeg: 244.8, decDeg: 5.2, magnitude: 0.4, constellation: "Canis Minor" },
  { name: "Rasalhague", shaDeg: 96.1, decDeg: 12.5, magnitude: 2.1, constellation: "Ophiuchus" },
  { name: "Regulus", shaDeg: 207.4, decDeg: 11.9, magnitude: 1.4, constellation: "Leo" },
  { name: "Rigel", shaDeg: 281.0, decDeg: -8.2, magnitude: 0.1, constellation: "Orion" },
  { name: "Rigil Kentaurus", shaDeg: 139.7, decDeg: -60.9, magnitude: -0.3, constellation: "Centaurus" },
  { name: "Sabik", shaDeg: 102.0, decDeg: -15.7, magnitude: 2.4, constellation: "Ophiuchus" },
  { name: "Schedar", shaDeg: 349.5, decDeg: 56.6, magnitude: 2.2, constellation: "Cassiopeia" },
  { name: "Shaula", shaDeg: 96.2, decDeg: -37.1, magnitude: 1.6, constellation: "Scorpius" },
  { name: "Sirius", shaDeg: 258.4, decDeg: -16.7, magnitude: -1.5, constellation: "Canis Major" },
  { name: "Spica", shaDeg: 158.3, decDeg: -11.3, magnitude: 1.0, constellation: "Virgo" },
  { name: "Suhail", shaDeg: 222.4, decDeg: -43.5, magnitude: 2.2, constellation: "Vela" },
  { name: "Vega", shaDeg: 80.6, decDeg: 38.8, magnitude: 0.0, constellation: "Lyra" },
  { name: "Zubenelgenubi", shaDeg: 137.0, decDeg: -16.0, magnitude: 2.7, constellation: "Libra" },
]);

/// Hemisphere a star sits in based on its declination.
export function hemisphereOf(star: NavStar): Hemisphere {
  return star.decDeg >= 0 ? "N" : "S";
}

/// A star is *broadly* observable for a navigator at latitude `lat` if
/// it ever rises above the horizon at that latitude. Stars with
/// `|Dec − Lat| < 90°` cross the horizon at some point in the night.
/// (Exact altitude maths still depends on time of year and night.)
export function visibleAtLatitude(star: NavStar, latDeg: number): boolean {
  return Math.abs(star.decDeg - latDeg) < 90;
}
